import axios from 'axios'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  // Separate axios instance for the refresh call so a failing refresh
  // cannot re-enter the response interceptor and loop forever.
  refreshClient = axios.create()

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // ** Interceptor callbacks
    const requestInterceptor = config => {
      // ** Get token from localStorage
      const accessToken = this.getToken()

      // ** If token is present add it to request's Authorization Header
      if (accessToken) {
        // ** eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
      } else {
        // Remove statically defined header if token is null (prevents "Bearer null" errors)
        if (config.headers && config.headers.Authorization) {
          delete config.headers.Authorization
        }
      }
      return config
    }

    const responseInterceptorError = error => {
      const { config, response } = error
      const originalRequest = config

      if (!response || response.status !== 401 || !originalRequest || originalRequest._retry) {
        return Promise.reject(error)
      }
      originalRequest._retry = true

      if (!this.isAlreadyFetchingAccessToken) {
        this.isAlreadyFetchingAccessToken = true
        this.refreshToken()
          .then(r => {
            this.isAlreadyFetchingAccessToken = false
            this.setToken(r.data.access)
            if (r.data.refresh) this.setRefreshToken(r.data.refresh)
            this.onAccessTokenFetched(r.data.access)
          })
          .catch(() => {
            this.isAlreadyFetchingAccessToken = false
            this.subscribers = []
            this.forceLogout()
          })
      }

      return new Promise(resolve => {
        this.addSubscriber(accessToken => {
          originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
          resolve(axios(originalRequest))
        })
      })
    }

    // ** Add request/response interceptor to global axios instance
    axios.interceptors.request.use(requestInterceptor, error => Promise.reject(error))
    axios.interceptors.response.use(response => response, responseInterceptorError)

    /**
     * MONKEY PATCH: axios.create
     *
     * Many components in this codebase create local axios instances via axios.create().
     * By default, those instances bypass the global interceptors and statically bind
     * the token from localStorage at module evaluation time (often leading to "null" tokens).
     *
     * This patch ensures that all future instances created via axios.create() automatically
     * inherit the JWT request/refresh logic while preserving their custom configurations
     * (e.g., custom timeouts or multipart/form-data headers).
     */
    const originalCreate = axios.create
    axios.create = (config = {}) => {
      // Scrub statically defined Authorization to avoid stale or null tokens
      if (config.headers && config.headers.Authorization) {
        delete config.headers.Authorization
      }

      const instance = originalCreate.call(axios, config)
      instance.interceptors.request.use(requestInterceptor, error => Promise.reject(error))
      instance.interceptors.response.use(response => response, responseInterceptorError)

      return instance
    }
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  forceLogout() {
    localStorage.removeItem('userData')
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    window.location.href = '/login'
  }

  login(...args) {
    return axios.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return axios.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.refreshClient.post(this.jwtConfig.refreshEndpoint, {
      refresh: this.getRefreshToken()
    })
  }

  logout() {
    return axios.post(this.jwtConfig.logoutEndpoint, {
      refresh: this.getRefreshToken()
    })
  }
}
